import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@seapoint/client/src/design-system/button/button';
import { Inline } from '@seapoint/client/src/design-system/inline/inline';
import { Stack } from '@seapoint/client/src/design-system/stack/stack';
import {
  AccountBankingTypeView,
  EmployeeView,
  PayrollRecordView,
  PayrollView,
} from '@seapoint/client/src/types/entity-types';
import { getAmountWithCurrency } from '@seapoint/client/src/utils/get-amount-with-currency';
import { Currency, FinancialServiceProviderAdapters } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { User } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { PayrollEmployee } from '../../components/financial-information-step/financial-information-table-column-definition';
import { usePayrollRecord } from '../../hooks/use-payroll-record';
import { EmployeeDrawerMode, usePayrollFlow } from '../../use-payroll-flow';
import { PayrollDraftTable } from './components/payroll-draft-table';

const payrollEmployeeSchema = z.object({
  employees: z.array(
    z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      destination: z
        .object({
          iban: z.string().nullable().optional(),
          accountNumber: z.string().nullable().optional(),
          sortCode: z.string().nullable().optional(),
          type: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
      reference: z.string().min(1, 'Reference is required'),
      netPay: z.string(),
    }),
  ),
});

export type PayrollFormData = z.infer<typeof payrollEmployeeSchema>;

type PayrollDraftWrapperProps = {
  payrollFlow: ReturnType<typeof usePayrollFlow>;
  currentPayroll: PayrollView | null;
  employees: EmployeeView[];
  currency: Currency;
  accounts: AccountBankingTypeView[];
  updatePayrollRecords: ReturnType<typeof usePayrollRecord>['updatePayrollRecords'];
  addPayrollRecords: ReturnType<typeof usePayrollRecord>['addPayrollRecords'];
  deletePayrollRecords: ReturnType<typeof usePayrollRecord>['deletePayrollRecords'];
  onContinue: () => void;
  openEmployeeDrawer: (mode: EmployeeDrawerMode, employeeId?: string) => void;
};

export const PayrollDraftWrapper = ({
  payrollFlow,
  currentPayroll,
  employees,
  currency,
  accounts,
  updatePayrollRecords,
  addPayrollRecords,
  deletePayrollRecords,
  onContinue,
  openEmployeeDrawer,
}: PayrollDraftWrapperProps) => {
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>(payrollFlow?.selectedRowIds || []);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const currentPayrollRecordsRef = useRef<PayrollRecordView[] | null>(null);

  useEffect(() => {
    currentPayrollRecordsRef.current = currentPayroll?.records || null;
  }, [currentPayroll]);

  const getFormValues = useCallback(
    () => ({
      employees: employees.map((employee) => {
        const existingRecord = currentPayroll?.records?.find((record) => record.employeeId === employee.id);
        return {
          id: employee.id,
          firstName: employee.firstName,
          lastName: employee.lastName,
          destination: employee.destination,
          reference: payrollFlow?.currentPayroll?.reference ?? '',
          netPay: String(existingRecord?.netPay || ''),
        };
      }),
    }),
    [employees, currentPayroll],
  );

  const form = useForm<PayrollFormData>({
    resolver: zodResolver(payrollEmployeeSchema),
    defaultValues: getFormValues(),
  });

  useEffect(() => {
    form.reset(getFormValues());
  }, [getFormValues, form]);

  useEffect(() => {
    if (currentPayroll === null) return;
    if (currentPayroll?.records?.length > 0) {
      const selectedRecords = currentPayroll?.records.map((record) => record.employeeId) ?? [];
      setSelectedRowIds(selectedRecords);
    } else {
      setSelectedRowIds(employees.map((employee) => employee.id));
    }
  }, []);

  const { handleSubmit, watch, setValue } = form;

  const watchedEmployees = watch('employees') as PayrollEmployee[];

  const handleNetPayChange = (employeeId: string, netPay: string) => {
    const isSelectedRecord = selectedRowIds.includes(employeeId);
    if (!isSelectedRecord) {
      setSelectedRowIds([...selectedRowIds, employeeId]);
    }
    const employeeIndex = watchedEmployees.findIndex((emp) => emp.id === employeeId);
    if (employeeIndex !== -1) {
      setValue(`employees.${employeeIndex}.netPay`, netPay, { shouldValidate: true });
    }
  };

  const handleContinue = () => {
    const payrollRecords = currentPayroll?.records || [];
    const validFormRecordsWithNetPay = watchedEmployees
      .filter((emp) => selectedRowIds.includes(emp.id))
      .filter((emp) => Number(emp.netPay) > 0);
    const validPayrollRecords =
      payrollRecords?.filter((record) => validFormRecordsWithNetPay.find((emp) => emp.id === record.employeeId)) || [];
    const recordsToDelete =
      payrollRecords?.filter((a) => !validPayrollRecords.find((b) => b.id === a.id)).map((c) => c.id) || [];
    const recordsToAdd = validFormRecordsWithNetPay
      .filter((emp) => !payrollRecords.find((record) => record.employeeId === emp.id))
      .map((emp) => emp.id);
    submitForm(validFormRecordsWithNetPay, recordsToDelete, recordsToAdd);
  };

  const allSelectedEmployeesHaveNetPay = () => {
    return watchedEmployees
      .filter((emp) => selectedRowIds.includes(emp.id))
      .every((emp) => {
        const hasNetPay = emp.netPay && emp.netPay !== '';
        if (!hasNetPay) return false;
        const isValidNetPay = !isNaN(Number(emp.netPay)) && Number(emp.netPay) > 0;
        return isValidNetPay;
      });
  };

  const totalNetPay = watchedEmployees.reduce((acc, employee) => {
    const isEmployeeSelected = selectedRowIds.includes(employee.id);
    if (!isEmployeeSelected) return acc;
    if (employee.id && employee.netPay && employee.netPay !== '') {
      return acc + Number(employee.netPay);
    }
    return acc;
  }, 0);

  const formattedTotalNetPay = getAmountWithCurrency({
    amount: totalNetPay,
    currency,
    isAccountBalance: true,
  });

  const onSubmit = () => {
    if (selectedRowIds.length === 0) {
      toast.error('Please select at least one employee to continue');
      return;
    }
    if (!allSelectedEmployeesHaveNetPay()) {
      toast.error('Add net pay for all selected employees');
      return;
    }
    handleContinue();
  };

  const submitForm = async (
    formDataRecords: PayrollFormData['employees'] = [],
    recordsToDelete: string[] = [],
    recordsToAdd: string[] = [],
  ) => {
    setIsSubmittingForm(true);
    try {
      const firstEligibleModulrAccount = accounts.find(
        (account) =>
          account.fspAdapter === FinancialServiceProviderAdapters.MODULR &&
          account.currency === payrollFlow?.currentPayroll?.currency,
      );
      const sourceAccountConfig =
        firstEligibleModulrAccount && firstEligibleModulrAccount.fspAdapter
          ? {
              isPresent: true as const,
              id: firstEligibleModulrAccount.id,
              fspAdapter: firstEligibleModulrAccount.fspAdapter,
            }
          : {
              isPresent: false as const,
            };
      const payrollId = payrollFlow?.currentPayroll?.id as string;
      if (recordsToDelete.length > 0) {
        await deletePayrollRecords(payrollId, recordsToDelete);
      }
      if (recordsToAdd.length > 0) {
        await addPayrollRecords(
          payrollId,
          recordsToAdd.map((employeeId) => ({ employeeId })),
        );
      }
      const payrollCurrency = payrollFlow?.currentPayroll?.currency as Currency;
      await updatePayrollRecords(
        payrollId,
        currentPayrollRecordsRef.current || [],
        formDataRecords,
        sourceAccountConfig,
        payrollCurrency,
      );
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          payrollId: payrollFlow?.currentPayroll?.id,
          payrollReference: payrollFlow?.currentPayroll?.reference,
        },
        tags: {
          component: 'PayrollDraftWrapper',
          action: 'submitForm',
        },
      });
      toast.error('Failed to save payroll records. Please try again.');
      setIsSubmittingForm(false);
      return;
    }
    setIsSubmittingForm(false);
    onContinue();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Inline className="mt-4 w-full justify-end">
          <Button
            type="button"
            iconLeft={<User size={16} />}
            onClick={() => openEmployeeDrawer('ADD')}
            disabled={isSubmittingForm}
          >
            Add Employee
          </Button>
        </Inline>
        <Stack className="w-full">
          <Inline space={2} className="w-full" wrap="nowrap">
            <h2 className="text-xl font-semibold">Employees</h2>
          </Inline>
          <div className="flex flex-col gap-6">
            <PayrollDraftTable
              employees={watchedEmployees}
              selectedRowIds={selectedRowIds}
              formState={form.formState}
              onEditEmployee={(employeeId) => openEmployeeDrawer('EDIT', employeeId)}
              setSelectedRowIds={setSelectedRowIds}
              onNetPayChange={handleNetPayChange}
            />
          </div>
          <p className="mt-4 w-full pr-4 text-right text-xl font-medium">{formattedTotalNetPay}</p>
          <Inline className="mt-4 w-full justify-end">
            <Button type="submit" isLoading={isSubmittingForm} disabled={isSubmittingForm}>
              Continue
            </Button>
          </Inline>
        </Stack>
      </form>
    </FormProvider>
  );
};
